<script setup>
import { computed, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconTriangleWarning from '../icons/IconTriangleWarning.vue';
import { PORTAL_DESTINATIONS } from '../../helpers/bannerHelper';

/**
 * The agency's banner on Home (XN-4564): the prototype's `.alert` card in the
 * banner's colour. One component for Home and the staff editor's preview, so
 * staff see exactly what policyholders get. An image (Hunter, 2026-10-01)
 * sits right of the text and above it on a phone; one that fails to load
 * leaves the text card. With no button, nothing on the card is a link.
 */
const props = defineProps({
  banner: { type: Object, required: true },
  // The editor's preview: nothing navigates and nothing dismisses.
  preview: { type: Boolean, default: false },
  canDismiss: { type: Boolean, default: true },
});
const emit = defineEmits(['dismiss', 'cta']);

const TONE_LABEL = { safety: 'Safety', renewal: 'Renewal', announcement: 'Announcement' };
const COLORS = ['amber', 'red', 'blue', 'green', 'purple'];

const tag = computed(() => TONE_LABEL[props.banner.tone] ?? '');
const color = computed(() => (COLORS.includes(props.banner.color) ? props.banner.color : 'amber'));
const ctaUrl = computed(() => String(props.banner.cta_url || ''));
const hasCta = computed(() => !!(props.banner.cta_label && ctaUrl.value));
// Only the listed portal pages open inside the portal; anything else is a web address.
const portalPage = computed(() => PORTAL_DESTINATIONS.some((d) => d.path === ctaUrl.value));

const imageFailed = ref(false);
watch(() => props.banner.image_url, () => { imageFailed.value = false; });
const image = computed(() => (imageFailed.value ? '' : props.banner.image_url || ''));
</script>

<template>
  <section
    class="portal-banner"
    :class="`portal-banner--${color}`"
    data-xl-gen="northstar"
    data-testid="portal-banner"
    aria-label="From your agency"
  >
    <span class="portal-banner__icon" aria-hidden="true"><IconTriangleWarning color="currentColor" /></span>
    <div class="portal-banner__text">
      <p v-if="tag" class="portal-banner__tag">{{ tag }}</p>
      <h2 class="portal-banner__title">{{ banner.title }}</h2>
      <!-- Sanitized on the server (HtmlSanitizer::clean) and in the editor. -->
      <div class="portal-banner__body" v-html="banner.body" />
      <div v-if="hasCta || (canDismiss && !preview)" class="portal-banner__actions">
        <template v-if="hasCta">
          <span v-if="preview" class="portal-banner__cta">
            {{ banner.cta_label }}
            <IconChevronRight color="currentColor" width="8" height="14" />
          </span>
          <RouterLink v-else-if="portalPage" :to="ctaUrl" class="portal-banner__cta" @click="emit('cta')">
            {{ banner.cta_label }}
            <IconChevronRight color="currentColor" width="8" height="14" />
          </RouterLink>
          <a
            v-else
            :href="ctaUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="portal-banner__cta"
            @click="emit('cta')"
          >
            {{ banner.cta_label }}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M14 4h6v6" /><path d="M20 4 11 13" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
            </svg>
            <span class="portal-banner__sr">(opens in a new tab)</span>
          </a>
        </template>
        <button
          v-if="canDismiss && !preview"
          type="button"
          class="portal-banner__dismiss"
          data-testid="banner-dismiss"
          @click="emit('dismiss')"
        >
          Dismiss
        </button>
      </div>
    </div>
    <img v-if="image" :src="image" alt="" class="portal-banner__image" @error="imageFailed = true">
  </section>
</template>

<style scoped>
/* Prototype `.alert` / `.banlink` / `.dismiss` (build 29 Sep), coloured per banner. */
.portal-banner {
  --banner-fg: var(--xl-color-warning);
  --banner-bg: var(--xl-color-warning-container);
  display: flex;
  gap: 18px;
  padding: 22px 24px;
  margin-bottom: 14px;
  border-radius: var(--xl-radius-lg);
  background: var(--banner-bg);
  border: 1px solid color-mix(in srgb, var(--banner-fg) 20%, transparent);
  color: var(--xl-color-on-surface);
}
.portal-banner--red { --banner-fg: var(--xl-color-error); --banner-bg: var(--xl-color-error-container); }
.portal-banner--blue { --banner-fg: var(--xl-color-info); --banner-bg: var(--xl-color-info-container); }
.portal-banner--green { --banner-fg: var(--xl-color-success); --banner-bg: var(--xl-color-success-container); }
.portal-banner--purple { --banner-fg: var(--xl-color-primary); --banner-bg: var(--xl-color-primary-container); }

.portal-banner__icon {
  width: 46px; height: 46px; flex: 0 0 46px; border-radius: 14px;
  display: grid; place-items: center;
  background: color-mix(in srgb, var(--banner-fg) 13%, transparent);
  color: var(--banner-fg);
}
.portal-banner__text { flex: 1; min-width: 0; }
.portal-banner__tag {
  margin: 0 0 5px; font-size: 11.5px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--banner-fg);
}
.portal-banner__title { margin: 0 0 6px; font-size: 20px; line-height: 1.25; font-weight: 600; overflow-wrap: anywhere; }
.portal-banner__body {
  margin: 0 0 14px; white-space: pre-line; font-size: 14.5px; line-height: 1.55; color: var(--xl-color-on-surface-variant);
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere;
}
.portal-banner__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 20px; }
.portal-banner__cta {
  display: inline-flex; align-items: center; gap: 7px; font-size: 15px; font-weight: 600;
  color: var(--banner-fg); text-decoration: underline; text-underline-offset: 3px; text-decoration-thickness: 1.5px;
}
.portal-banner__cta:focus-visible, .portal-banner__dismiss:focus-visible {
  outline: 2px solid var(--banner-fg); outline-offset: 2px; border-radius: 4px;
}
.portal-banner__dismiss {
  border: 0; background: none; padding: 0; font: inherit; font-size: 14px; font-weight: 500;
  color: var(--xl-color-outline); cursor: pointer;
}
.portal-banner__dismiss:hover { color: var(--xl-color-on-surface); }
.portal-banner__image {
  width: 180px; height: 120px; flex: 0 0 180px; align-self: center;
  object-fit: cover; border-radius: 14px;
}
.portal-banner__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

@media (max-width: 560px) {
  .portal-banner { flex-wrap: wrap; padding: 18px; }
  .portal-banner__image { order: -1; width: 100%; flex-basis: 100%; height: 160px; }
}
</style>
